/**
 * Mask Layer Animation
 *
 * Animacja maski pasków odsłaniających element podczas scrollowania.
 * Używa GSAP i ScrollTrigger do animacji.
 */
(function () {
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
    console.warn("Mask Layer: GSAP or ScrollTrigger not found");
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  // Konfiguracja pasków maski
  const GRADIENT_BANDS = Array.from({ length: 20 }, (_, index) => [
    index * 5,
    (index + 1) * 5,
  ]);
  const BAND_GAP = 3;
  const BAND_COUNT = GRADIENT_BANDS.length;
  const AVAILABLE_PROGRESS = 100 - BAND_COUNT * BAND_GAP;

  /**
   * Ogranicza wartość do zakresu min-max
   */
  const clampValue = (value, min = 0, max = 1) =>
    Math.min(Math.max(value, min), max);

  /**
   * Tworzy gradient maski na podstawie postępu animacji
   * @param {number} progressPercent - Postęp animacji (0-100)
   * @param {string} direction - Kierunek animacji ('vertical' lub 'horizontal')
   * @returns {string} - CSS linear-gradient
   */
  function createMaskGradient(progressPercent, direction = "vertical") {
    const isScrollingDown = progressPercent > 50;
    const halfProgress =
      clampValue(progressPercent - (isScrollingDown ? 50 : 0), 0, 50) * 2;

    const stripes = GRADIENT_BANDS.map(([start, end], index) => {
      const bandIndex = isScrollingDown ? BAND_COUNT - 1 - index : index;
      const bandOffset = bandIndex * BAND_GAP;
      const bandSize = end - start;
      const bandRatio = clampValue(
        (halfProgress - bandOffset) / AVAILABLE_PROGRESS
      );
      const maskPosition = isScrollingDown
        ? end - bandRatio * bandSize
        : start + bandRatio * bandSize;

      return `rgba(0,0,0,1) ${start}% ${maskPosition}%, rgba(255,255,255,0) ${maskPosition}% ${end}%`;
    });

    // Kierunek gradientu w zależności od direction
    const gradientDirection =
      direction === "horizontal"
        ? isScrollingDown
          ? "-90deg"
          : "90deg"
        : isScrollingDown
        ? "180deg"
        : "0deg";
    return `linear-gradient(${gradientDirection}, ${stripes.join(",")})`;
  }

  /**
   * Konfiguruje animację maski dla pojedynczego elementu
   * @param {HTMLElement} maskElement - Element z atrybutem mask-layer
   */
  function setupMaskTransition(maskElement) {
    if (!maskElement) return;

    // Odczytaj parametry z data-atrybutów
    const duration =
      parseFloat(maskElement.getAttribute("data-mask-duration")) || 1.5;
    const useScrub = maskElement.getAttribute("data-mask-scrub") === "true";
    const direction =
      maskElement.getAttribute("data-mask-direction") || "vertical";

    // Stałe wartości trigger
    const triggerStart = "top top";
    const triggerEnd = useScrub ? "center top" : "bottom top";

    const maskTimeline = gsap.timeline({
      paused: true,
      onUpdate() {
        const progressPercent = maskTimeline.totalProgress() * 50;
        const gradient = createMaskGradient(progressPercent, direction);
        gsap.set(maskElement, {
          "--mask-gradient": gradient,
        });
      },
    });

    maskTimeline.to(maskElement, {
      visibility: "visible",
      duration: duration / 2,
    });

    // Twórz ScrollTrigger w zależności od ustawienia scrub
    if (useScrub) {
      ScrollTrigger.create({
        trigger: maskElement,
        start: triggerStart,
        end: triggerEnd,
        scrub: true,
        animation: maskTimeline,
      });
    } else {
      ScrollTrigger.create({
        trigger: maskElement,
        start: triggerStart,
        end: triggerEnd,
        onEnter() {
          maskTimeline.restart();
        },
        onLeaveBack() {
          maskTimeline.reverse();
        },
      });
    }
  }

  /**
   * Inicjalizuje animacje maski dla wszystkich elementów
   */
  function initMaskLayerAnimations() {
    const maskElements = document.querySelectorAll("[mask-layer]");

    if (!maskElements || !maskElements.length) {
      return;
    }

    maskElements.forEach(function (maskElement) {
      setupMaskTransition(maskElement);
    });
  }

  // Inicjalizacja po załadowaniu DOM
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initMaskLayerAnimations);
  } else {
    initMaskLayerAnimations();
  }

  // Eksportuj funkcje dla ewentualnego użycia zewnętrznego
  window.CEWMaskLayer = {
    init: initMaskLayerAnimations,
    createMaskGradient: createMaskGradient,
    setupMaskTransition: setupMaskTransition,
  };
})();
